'use client';

import { c as _c } from "react/compiler-runtime";
import { type Upload } from '@shared/all/domains/uploads';
import { type UploadItem } from '@shared/utils/hooks/useUpload';
import { Camera, Loader2, X } from 'lucide-react';
import Image from 'next/image';
import { getUploadSrc } from '../../utils/uploads';
import DocumentItem from '../common/DocumentItem';
import VideoPlayer from '../UI/Video';
type CombinedUpload = Upload | UploadItem;
type CameraPhoto = {
  uri: string;
  factiiiId: number;
};
type PendingFile = {
  key: string;
  fileName?: string;
};
type Props = {
  uploads: CombinedUpload[];
  removeAttachmentHandler: (id: string) => void;
  cameraPhotos?: CameraPhoto[];
  removeCameraPhotoHandler?: (factiiiId: number) => void;
  /** Keys of items currently uploading to server (show spinner overlay) */
  uploadingKeys?: string[];
  /** Files still being processed (show spinner placeholder) */
  pendingFiles?: PendingFile[];
};
const GalleryView = t0 => {
  const $ = _c(24);
  const {
    uploads,
    removeAttachmentHandler,
    cameraPhotos: t1,
    removeCameraPhotoHandler,
    uploadingKeys: t2,
    pendingFiles: t3
  } = t0;
  let t4;
  if ($[0] !== t1) {
    t4 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const cameraPhotos = t4;
  let t5;
  if ($[2] !== t2) {
    t5 = t2 === undefined ? [] : t2;
    $[2] = t2;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const uploadingKeys = t5;
  let t6;
  if ($[4] !== t3) {
    t6 = t3 === undefined ? [] : t3;
    $[4] = t3;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const pendingFiles = t6;
  const hasContent = uploads.length > 0 || cameraPhotos.length > 0 || pendingFiles.length > 0;
  if (!hasContent) {
    return null;
  }
  let t7;
  if ($[6] !== pendingFiles) {
    t7 = pendingFiles.map(_temp);
    $[6] = pendingFiles;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== cameraPhotos || $[9] !== removeCameraPhotoHandler) {
    let t9;
    if ($[11] !== removeCameraPhotoHandler) {
      t9 = photo => <div key={`camera-${photo.factiiiId}`} className="relative shrink-0 overflow-hidden rounded-lg h-[100px] w-[100px]"><Image src={photo.uri} alt="Camera capture" fill={true} className="rounded object-cover" unoptimized={true} /><div className="absolute bottom-1 left-1 rounded-full bg-black/60 p-1"><Camera size={12} className="text-white" /></div>{removeCameraPhotoHandler && <button onClick={() => removeCameraPhotoHandler(photo.factiiiId)} title="Remove photo" className="absolute right-1 top-1 rounded-full bg-background-secondary p-1 text-textPrimary hover:bg-ds-border-hover"><X size={15} /></button>}</div>;
      $[11] = removeCameraPhotoHandler;
      $[12] = t9;
    } else {
      t9 = $[12];
    }
    t8 = cameraPhotos.map(t9);
    $[8] = cameraPhotos;
    $[9] = removeCameraPhotoHandler;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[13] !== removeAttachmentHandler || $[14] !== uploadingKeys || $[15] !== uploads) {
    let t10;
    if ($[17] !== removeAttachmentHandler || $[18] !== uploadingKeys) {
      t10 = x => {
        let url;
        let type;
        let id;
        let isUploadObj = false;
        if ("view" in x && "type" in x && "id" in x) {
          url = getUploadSrc(x as Upload);
          type = (x as Upload).type;
          id = (x as Upload).id;
          isUploadObj = true;
        } else {
          const item = x as UploadItem;
          id = item.key;
          url = item.uri;
          const ext = item.fileName?.split(".").pop()?.toLowerCase() ?? "";
          if (["mp4", "mov", "mpeg"].includes(ext)) {
            type = "VIDEO";
          } else {
            if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext) || !ext) {
              type = "IMAGE";
            } else {
              type = "DOCUMENT";
            }
          }
        }
        const isUploading = uploadingKeys.includes(id);
        return <div key={id} className="relative shrink-0 overflow-hidden rounded-lg h-[100px] w-[100px]">{type === "IMAGE" && <Image src={url} alt="" fill={true} className="rounded object-cover" />}{type === "VIDEO" && <VideoPlayer src={url} preload="metadata" videoSize={{
            width: 100,
            height: 100
          }} className="h-full w-full object-cover" />}{type === "DOCUMENT" && (isUploadObj ? <div className="flex h-full w-full items-center justify-center p-2"><DocumentItem data={x as Upload} /></div> : <div className="flex h-full w-full flex-col items-center justify-center p-2 text-center"><div className="mb-1 text-2xl">📄</div><div className="max-w-full truncate text-xs text-textSecondary">{(x as UploadItem).fileName ?? "file"}</div></div>)}{isUploading && <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/60"><Loader2 className="size-8 animate-spin text-white" /></div>}<button onClick={() => removeAttachmentHandler(id)} title="Close Button" disabled={isUploading} className="absolute right-1 top-1 rounded-full bg-background-secondary p-1 text-textPrimary hover:bg-ds-border-hover disabled:pointer-events-none disabled:opacity-60"><X size={15} /></button></div>;
      };
      $[17] = removeAttachmentHandler;
      $[18] = uploadingKeys;
      $[19] = t10;
    } else {
      t10 = $[19];
    }
    t9 = uploads.map(t10);
    $[13] = removeAttachmentHandler;
    $[14] = uploadingKeys;
    $[15] = uploads;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[20] !== t7 || $[21] !== t8 || $[22] !== t9) {
    t10 = <div className="mt-2 w-full rounded-lg"><div className="flex flex-wrap gap-2">{t7}{t8}{t9}</div></div>;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  return t10;
};
export default GalleryView;
function _temp(pf) {
  return <div key={`pending-${pf.key}`} className="flex shrink-0 flex-col items-center justify-center gap-1 overflow-hidden rounded-lg bg-background-secondary h-[100px] w-[100px]"><Loader2 className="size-8 animate-spin text-textSecondary" /><span className="max-w-full truncate px-1 text-xs text-textSecondary">{pf.fileName ?? "\u2026"}</span></div>;
}